// src/modules/units/tyres/shared/tyre-xlsx-write.js
//
// A small, focused .xlsx WRITER — the counterpart of tyre-import-file.js's reader.
// Pure: it turns a plain workbook description into OOXML parts and zips them with
// an INJECTED JSZip, so it runs in the Node harness as well as the browser.
//
// Why hand-written rather than a library: the module already depends on jszip
// (lazy-loaded), the npm SheetJS build is frozen with published advisories, and
// what the import template needs is narrow and well-specified — inline-string and
// number cells, a handful of named styles, column defaults, a frozen header,
// hidden sheets and data validations (dropdown lists, number/date rules, custom
// formulas). That is ~300 lines we own and test, not a dependency we audit.
//
// Workbook description:
//   {
//     creator?, title?,
//     definedNames?: [{ name, ref, hidden? }],  // workbook names, e.g.
//                                 //   { name: 'PT_SuggestKey', ref: 'PT_Lists!$N$2:$N$29' }
//     sheets: [{
//       name,                     // ≤ 31 chars, no []:*?/\
//       hidden?: boolean,         // state="hidden" (the importer skips these)
//       selected?: boolean,       // the tab that opens first
//       freezeRows?: number,      // frozen header rows
//       cols?: [{ width?, style? }],          // per column, in order (A, B, …)
//       rows: [[cell, …], …],                 // cell = value | { v, s } | null
//       rowHeights?: { [rowIndex0]: points },
//       merges?: ['A1:D1', …],
//       formulaColumns?: [{       // one formula down a column, written as ONE
//         col,                    //   shared formula (compact, and Excel keeps
//         formula,                //   it shared): `formula` is for the first
//         fromRow, toRow,         //   row (0-based fromRow), relative refs shift
//         style?                  //   down like a fill-down. No leading '='.
//       }],                       //   It must NOT refer to another sheet (see
//                                 //   assertShareable) — use a definedName.
//       conditionalFormats?: [{ sqref, formula, format: 'suggestion' }],
//                                 // expression rule; 'suggestion' = grey italic
//       validations?: [{
//         sqref,                  // 'A2:A1001'
//         type,                   // 'list' | 'decimal' | 'whole' | 'date' | 'custom' | 'textLength'
//         operator?,              // 'between' | 'greaterThanOrEqual' | …
//         formula1, formula2?,    // WITHOUT the leading '='
//         errorStyle?,            // 'stop' (default) | 'warning' | 'information'
//         showErrorMessage?,      // default true
//         allowBlank?,            // default true
//         errorTitle?, error?, promptTitle?, prompt?
//       }]
//     }]
//   }
//
// Named styles (STYLE below) are the only styles; a cell or column names one.

export const STYLE = Object.freeze({
  DEFAULT: 'default',
  HEADER: 'header',          // white bold on brand blue, wrapped, bordered
  HEADER_REQUIRED: 'headerRequired', // dark bold on accent green
  TEXT: 'text',              // number format '@' — Excel keeps "0012" as typed
  DATE: 'date',              // yyyy-mm-dd
  MONEY: 'money',            // 0.00
  TITLE: 'title',            // large bold
  BOLD: 'bold',              // bold, top-aligned (row labels beside wrapped text)
  WRAP: 'wrap',              // wrapped, top-aligned (prose cells)
  TABLE_HEAD: 'tableHead',   // bold on light grey, bottom border
  MUTED: 'muted'             // grey italic
});

const STYLE_INDEX = Object.freeze({
  [STYLE.DEFAULT]: 0,
  [STYLE.HEADER]: 1,
  [STYLE.HEADER_REQUIRED]: 2,
  [STYLE.TEXT]: 3,
  [STYLE.DATE]: 4,
  [STYLE.MONEY]: 5,
  [STYLE.TITLE]: 6,
  [STYLE.BOLD]: 7,
  [STYLE.WRAP]: 8,
  [STYLE.TABLE_HEAD]: 9,
  [STYLE.MUTED]: 10
});

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** 0 → "A", 26 → "AA" */
export function colLetter(i) {
  let n = i + 1;
  let s = '';
  while (n > 0) { const r = (n - 1) % 26; s = String.fromCharCode(65 + r) + s; n = Math.floor((n - 1) / 26); }
  return s;
}

/** 'A', 0-based row → 'A1' */
export const cellRef = (col, row) => `${colLetter(col)}${row + 1}`;

// XML 1.0 forbids most C0 control characters; drop them rather than emit a file
// Excel refuses to open.
// eslint-disable-next-line no-control-regex
const XML_INVALID = /[\u0000-\u0008\u000B\u000C\u000E-\u001F￾￿]/g;

export function xmlEscape(s) {
  return String(s ?? '')
    .replace(XML_INVALID, '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

const clip = (s, n) => {
  const t = String(s ?? '');
  return t.length > n ? `${t.slice(0, n - 1)}…` : t;
};

/** A sheet name Excel will accept: ≤ 31 chars, none of []:*?/\ . */
export function safeSheetName(name) {
  const s = String(name || 'Sheet').replace(/[[\]:*?/\\]/g, ' ').trim();
  return (s || 'Sheet').slice(0, 31);
}

// A defined name Excel accepts: letter, _ or \ first, then letters, digits, _ .;
// and not something Excel would read as a cell reference (A1 or R1C1 style).
const NAME_SHAPE = /^[A-Za-z_\\][A-Za-z0-9_.]{0,254}$/;
const LOOKS_LIKE_REF = /^([A-Za-z]{1,3}\d+|[RrCc]|[Rr]\d*[Cc]\d*)$/;

export function isValidDefinedName(name) {
  const s = String(name || '');
  return NAME_SHAPE.test(s) && !LOOKS_LIKE_REF.test(s) && !/^(true|false)$/i.test(s);
}

// Text of a formula with its "string literals" blanked, so a '!' inside quotes is
// not mistaken for a sheet reference.
const withoutStrings = (formula) => String(formula).replace(/"(?:[^"]|"")*"/g, '""');

/**
 * Excel forbids a SHARED formula from referring to another sheet
 * ([MS-XLSB] 2.5.97.88 SharedParsedFormula: MUST NOT contain PtgRef3d/PtgArea3d …).
 * It opens such a file only after a "repair" that deletes every one of them.
 * Fail here instead, where a test sees it; refer to a definedName instead.
 */
export function assertShareable(formula) {
  if (withoutStrings(formula).includes('!')) {
    throw new Error('A shared formula cannot refer to another sheet (Excel would delete it); refer to a defined name instead.');
  }
}

/**
 * The formula as it reads `dRows` rows further down — what a fill-down does:
 * relative row numbers in A1 references move, $-anchored ones stay. String
 * literals, names (PT_SuggestKey) and function names (LOG10() are untouched.
 * Covers the A1 references this writer's callers produce (no R1C1, no whole-
 * row 2:2 ranges).
 */
export function shiftFormulaRows(formula, dRows) {
  if (!dRows) return String(formula);
  return String(formula).split(/("(?:[^"]|"")*")/).map((part, i) => (i % 2
    ? part // inside "…"
    : part.replace(/(^|[^A-Za-z0-9_.\\])(\$?[A-Za-z]{1,3})(\$?)(\d+)(?![A-Za-z0-9_.(])/g,
      (m, pre, col, abs, row) => (abs ? m : `${pre}${col}${Number(row) + dRows}`))
  )).join('');
}

/** Quote a sheet name for use in a formula reference when needed. */
export function sheetRef(name) {
  return /^[A-Za-z_][A-Za-z0-9_]*$/.test(name) ? name : `'${String(name).replace(/'/g, "''")}'`;
}

// ---------------------------------------------------------------------------
// Parts
// ---------------------------------------------------------------------------

function cellXml(cell, col, row) {
  if (cell === null || cell === undefined || cell === '') return '';
  const obj = typeof cell === 'object' && !(cell instanceof Date) ? cell : { v: cell };
  const s = STYLE_INDEX[obj.s] ?? 0;
  const sAttr = s ? ` s="${s}"` : '';
  const r = cellRef(col, row);
  const v = obj.v;
  if (v === null || v === undefined || v === '') return s ? `<c r="${r}"${sAttr}/>` : '';
  if (typeof v === 'number' && Number.isFinite(v)) return `<c r="${r}"${sAttr}><v>${v}</v></c>`;
  if (typeof v === 'boolean') return `<c r="${r}"${sAttr} t="b"><v>${v ? 1 : 0}</v></c>`;
  return `<c r="${r}"${sAttr} t="inlineStr"><is><t xml:space="preserve">${xmlEscape(v)}</t></is></c>`;
}

function validationXml(dv) {
  const attrs = [
    `type="${dv.type}"`,
    dv.operator ? `operator="${dv.operator}"` : '',
    `errorStyle="${dv.errorStyle || 'stop'}"`,
    `allowBlank="${dv.allowBlank === false ? 0 : 1}"`,
    'showInputMessage="1"',
    `showErrorMessage="${dv.showErrorMessage === false ? 0 : 1}"`,
    // Excel's limits: titles 32, messages 255. Over-long values corrupt the file.
    dv.errorTitle ? `errorTitle="${xmlEscape(clip(dv.errorTitle, 32))}"` : '',
    dv.error ? `error="${xmlEscape(clip(dv.error, 255))}"` : '',
    dv.promptTitle ? `promptTitle="${xmlEscape(clip(dv.promptTitle, 32))}"` : '',
    dv.prompt ? `prompt="${xmlEscape(clip(dv.prompt, 255))}"` : '',
    `sqref="${dv.sqref}"`
  ].filter(Boolean).join(' ');
  const f1 = dv.formula1 !== undefined ? `<formula1>${xmlEscape(dv.formula1)}</formula1>` : '';
  const f2 = dv.formula2 !== undefined ? `<formula2>${xmlEscape(dv.formula2)}</formula2>` : '';
  return `<dataValidation ${attrs}>${f1}${f2}</dataValidation>`;
}

// Differential formats for conditional formatting (styles.xml <dxfs>).
const DXF_INDEX = Object.freeze({ suggestion: 0 });

// A typed value in a formula column replaces the formula in that cell.
const isTyped = (cell) => {
  if (cell === null || cell === undefined || cell === '') return false;
  const v = typeof cell === 'object' && !(cell instanceof Date) ? cell.v : cell;
  return !(v === null || v === undefined || v === '');
};

// Formula cells per row from the sheet's formulaColumns, each as [col, xml].
// The shared formula's MASTER (the cell holding its text) is the first cell of
// the column NOT typed over: dependants that point at a master that was never
// written lose their formula. The text is shifted to the master's row.
function formulaCellsByRow(sheet) {
  const byRow = new Map();
  const rows = sheet.rows || [];
  let si = 0;
  for (const fc of sheet.formulaColumns || []) {
    assertShareable(fc.formula);
    const s = STYLE_INDEX[fc.style] ?? 0;
    const sAttr = s ? ` s="${s}"` : '';
    const free = [];
    for (let r = fc.fromRow; r <= fc.toRow; r++) if (!isTyped(rows[r]?.[fc.col])) free.push(r);
    if (!free.length) continue;
    const master = free[0];
    const ref = `${cellRef(fc.col, master)}:${cellRef(fc.col, fc.toRow)}`;
    const text = xmlEscape(shiftFormulaRows(fc.formula, master - fc.fromRow));
    for (const r of free) {
      const f = r === master
        ? `<f t="shared" ref="${ref}" si="${si}">${text}</f>`
        : `<f t="shared" si="${si}"/>`;
      if (!byRow.has(r)) byRow.set(r, []);
      byRow.get(r).push([fc.col, `<c r="${cellRef(fc.col, r)}"${sAttr}>${f}</c>`]);
    }
    si++;
  }
  return byRow;
}

function sheetXml(sheet) {
  const rows = [...(sheet.rows || [])];
  const formulas = formulaCellsByRow(sheet);
  const lastFormulaRow = formulas.size ? Math.max(...formulas.keys()) : -1;
  while (rows.length <= lastFormulaRow) rows.push([]);
  const width = Math.max(1, (sheet.cols || []).length, ...rows.map((r) => (r || []).length));
  const dim = `A1:${cellRef(width - 1, Math.max(0, rows.length - 1))}`;

  const freeze = sheet.freezeRows > 0;
  const top = cellRef(0, sheet.freezeRows || 0);
  const view = freeze
    ? `<sheetView workbookViewId="0"${sheet.selected ? ' tabSelected="1"' : ''}><pane ySplit="${sheet.freezeRows}" topLeftCell="${top}" activePane="bottomLeft" state="frozen"/><selection pane="bottomLeft" activeCell="${top}" sqref="${top}"/></sheetView>`
    : `<sheetView workbookViewId="0"${sheet.selected ? ' tabSelected="1"' : ''}/>`;

  const cols = (sheet.cols || []).map((c, i) => {
    const w = c?.width ? ` width="${c.width}" customWidth="1"` : '';
    const s = c?.style ? ` style="${STYLE_INDEX[c.style] ?? 0}"` : '';
    return w || s ? `<col min="${i + 1}" max="${i + 1}"${w || ' width="12"'}${s}/>` : '';
  }).join('');

  const data = rows.map((r, ri) => {
    // Typed cells and formula cells merged in column order (Excel requires it).
    const typed = (r || []).map((c, ci) => [ci, cellXml(c, ci, ri)]).filter(([, x]) => x);
    // formulaCellsByRow already skipped typed cells; a styled-but-empty cell
    // (<c s=".."/>) gives way to the formula.
    const fcells = formulas.get(ri) || [];
    const fcols = new Set(fcells.map(([col]) => col));
    const kept = typed.filter(([ci]) => !fcols.has(ci));
    const cells = [...kept, ...fcells].sort((a, b) => a[0] - b[0]).map(([, x]) => x).join('');
    const ht = sheet.rowHeights?.[ri];
    const htAttr = ht ? ` ht="${ht}" customHeight="1"` : '';
    return cells || htAttr ? `<row r="${ri + 1}"${htAttr}>${cells}</row>` : '';
  }).join('');

  const merges = (sheet.merges || []).length
    ? `<mergeCells count="${sheet.merges.length}">${sheet.merges.map((m) => `<mergeCell ref="${m}"/>`).join('')}</mergeCells>`
    : '';
  const cfs = (sheet.conditionalFormats || []).map((cf, i) =>
    `<conditionalFormatting sqref="${cf.sqref}"><cfRule type="expression" dxfId="${DXF_INDEX[cf.format] ?? 0}" priority="${i + 1}"><formula>${xmlEscape(cf.formula)}</formula></cfRule></conditionalFormatting>`
  ).join('');
  const dvs = (sheet.validations || []).length
    ? `<dataValidations count="${sheet.validations.length}">${sheet.validations.map(validationXml).join('')}</dataValidations>`
    : '';

  // Element order is fixed by the schema (CT_Worksheet); Excel rejects others.
  return '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n' +
    '<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">' +
    `<dimension ref="${dim}"/>` +
    `<sheetViews>${view}</sheetViews>` +
    '<sheetFormatPr defaultRowHeight="15"/>' +
    (cols ? `<cols>${cols}</cols>` : '') +
    `<sheetData>${data}</sheetData>` +
    merges +
    cfs +
    dvs +
    '<pageMargins left="0.7" right="0.7" top="0.75" bottom="0.75" header="0.3" footer="0.3"/>' +
    '</worksheet>';
}

const STYLES_XML = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n' +
  '<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">' +
  '<numFmts count="1"><numFmt numFmtId="164" formatCode="yyyy-mm-dd"/></numFmts>' +
  '<fonts count="5">' +
  '<font><sz val="11"/><name val="Calibri"/><family val="2"/></font>' +
  '<font><b/><sz val="11"/><name val="Calibri"/><family val="2"/></font>' +
  '<font><b/><sz val="11"/><color rgb="FFFFFFFF"/><name val="Calibri"/><family val="2"/></font>' +
  '<font><b/><sz val="16"/><color rgb="FF185785"/><name val="Calibri"/><family val="2"/></font>' +
  '<font><i/><sz val="10"/><color rgb="FF6B7A8D"/><name val="Calibri"/><family val="2"/></font>' +
  '</fonts>' +
  '<fills count="5">' +
  '<fill><patternFill patternType="none"/></fill>' +
  '<fill><patternFill patternType="gray125"/></fill>' +
  '<fill><patternFill patternType="solid"><fgColor rgb="FF185785"/><bgColor indexed="64"/></patternFill></fill>' +
  '<fill><patternFill patternType="solid"><fgColor rgb="FFAECE25"/><bgColor indexed="64"/></patternFill></fill>' +
  '<fill><patternFill patternType="solid"><fgColor rgb="FFEEF2F6"/><bgColor indexed="64"/></patternFill></fill>' +
  '</fills>' +
  '<borders count="2">' +
  '<border><left/><right/><top/><bottom/><diagonal/></border>' +
  '<border><left/><right/><top/><bottom style="thin"><color rgb="FFC9D5E2"/></bottom><diagonal/></border>' +
  '</borders>' +
  '<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>' +
  '<cellXfs count="11">' +
  /* 0 default */ '<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>' +
  /* 1 header */ '<xf numFmtId="49" fontId="2" fillId="2" borderId="1" xfId="0" applyNumberFormat="1" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment vertical="center" wrapText="1"/></xf>' +
  /* 2 headerRequired */ '<xf numFmtId="49" fontId="1" fillId="3" borderId="1" xfId="0" applyNumberFormat="1" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment vertical="center" wrapText="1"/></xf>' +
  /* 3 text */ '<xf numFmtId="49" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>' +
  /* 4 date */ '<xf numFmtId="164" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>' +
  /* 5 money */ '<xf numFmtId="2" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>' +
  /* 6 title */ '<xf numFmtId="0" fontId="3" fillId="0" borderId="0" xfId="0" applyFont="1"/>' +
  /* 7 bold */ '<xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1" applyAlignment="1"><alignment vertical="top"/></xf>' +
  /* 8 wrap */ '<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0" applyAlignment="1"><alignment vertical="top" wrapText="1"/></xf>' +
  /* 9 tableHead */ '<xf numFmtId="0" fontId="1" fillId="4" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment vertical="top" wrapText="1"/></xf>' +
  /* 10 muted */ '<xf numFmtId="0" fontId="4" fillId="0" borderId="0" xfId="0" applyFont="1" applyAlignment="1"><alignment vertical="top" wrapText="1"/></xf>' +
  '</cellXfs>' +
  '<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>' +
  // dxf 0 "suggestion": grey italic on a faint fill — a value the template filled in.
  // Excel hides gridlines under any fill, so the cell gets thin borders in the
  // gridline grey and the grid stays continuous (CT_Dxf order: font, fill, border).
  '<dxfs count="1"><dxf><font><i/><color rgb="FF6B7A8D"/></font><fill><patternFill><bgColor rgb="FFF4F7FA"/></patternFill></fill>' +
  '<border><left style="thin"><color rgb="FFD9D9D9"/></left><right style="thin"><color rgb="FFD9D9D9"/></right>' +
  '<top style="thin"><color rgb="FFD9D9D9"/></top><bottom style="thin"><color rgb="FFD9D9D9"/></bottom></border></dxf></dxfs>' +
  '</styleSheet>';

/**
 * Build the .xlsx bytes.
 * @param {object} book   workbook description (see header)
 * @param {object} JSZip  the JSZip constructor
 * @param {{now?: Date}} [opts]
 * @returns {Promise<Uint8Array>}
 */
export async function writeXlsx(book, JSZip, { now = new Date() } = {}) {
  const sheets = (book.sheets || []).map((s, i) => ({ ...s, name: safeSheetName(s.name), index: i + 1 }));
  if (!sheets.length) throw new Error('A workbook needs at least one sheet.');
  const names = new Set();
  for (const s of sheets) {
    const k = s.name.toLowerCase();
    if (names.has(k)) throw new Error(`Duplicate sheet name: ${s.name}`);
    names.add(k);
  }
  if (sheets.every((s) => s.hidden)) throw new Error('At least one sheet must be visible.');
  const definedNames = book.definedNames || [];
  const nameKeys = new Set();
  for (const d of definedNames) {
    if (!isValidDefinedName(d.name)) throw new Error(`Invalid defined name: ${d.name}`);
    if (!d.ref) throw new Error(`Defined name ${d.name} has no reference.`);
    const k = d.name.toLowerCase(); // names are case-insensitive in Excel
    if (nameKeys.has(k)) throw new Error(`Duplicate defined name: ${d.name}`);
    nameKeys.add(k);
  }
  const active = Math.max(0, sheets.findIndex((s) => s.selected && !s.hidden));

  const zip = new JSZip();
  const iso = now.toISOString().replace(/\.\d{3}Z$/, 'Z');

  zip.file('[Content_Types].xml',
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n' +
    '<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">' +
    '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>' +
    '<Default Extension="xml" ContentType="application/xml"/>' +
    '<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>' +
    sheets.map((s) => `<Override PartName="/xl/worksheets/sheet${s.index}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`).join('') +
    '<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>' +
    '<Override PartName="/docProps/core.xml" ContentType="application/vnd.openxmlformats-package.core-properties+xml"/>' +
    '<Override PartName="/docProps/app.xml" ContentType="application/vnd.openxmlformats-officedocument.extended-properties+xml"/>' +
    '</Types>');

  zip.file('_rels/.rels',
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n' +
    '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
    '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>' +
    '<Relationship Id="rId2" Type="http://schemas.openxmlformats.org/package/2006/relationships/metadata/core-properties" Target="docProps/core.xml"/>' +
    '<Relationship Id="rId3" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/extended-properties" Target="docProps/app.xml"/>' +
    '</Relationships>');

  zip.file('docProps/core.xml',
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n' +
    '<cp:coreProperties xmlns:cp="http://schemas.openxmlformats.org/package/2006/metadata/core-properties" xmlns:dc="http://purl.org/dc/elements/1.1/" xmlns:dcterms="http://purl.org/dc/terms/" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance">' +
    `<dc:title>${xmlEscape(book.title || '')}</dc:title>` +
    `<dc:creator>${xmlEscape(book.creator || 'PressureTrack')}</dc:creator>` +
    `<dcterms:created xsi:type="dcterms:W3CDTF">${iso}</dcterms:created>` +
    `<dcterms:modified xsi:type="dcterms:W3CDTF">${iso}</dcterms:modified>` +
    '</cp:coreProperties>');

  zip.file('docProps/app.xml',
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n' +
    '<Properties xmlns="http://schemas.openxmlformats.org/officeDocument/2006/extended-properties"><Application>PressureTrack TPM</Application></Properties>');

  zip.file('xl/workbook.xml',
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n' +
    '<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">' +
    `<bookViews><workbookView activeTab="${active}"/></bookViews>` +
    '<sheets>' +
    sheets.map((s) => `<sheet name="${xmlEscape(s.name)}" sheetId="${s.index}"${s.hidden ? ' state="hidden"' : ''} r:id="rId${s.index}"/>`).join('') +
    '</sheets>' +
    // CT_Workbook order: sheets → definedNames → calcPr.
    (definedNames.length
      ? `<definedNames>${definedNames.map((d) => `<definedName name="${xmlEscape(d.name)}"${d.hidden ? ' hidden="1"' : ''}>${xmlEscape(d.ref)}</definedName>`).join('')}</definedNames>`
      : '') +
    // Formula cells are written without cached results, so Excel must calculate
    // them on open (otherwise suggestions would show blank until a recalc).
    '<calcPr calcId="191029" fullCalcOnLoad="1"/>' +
    '</workbook>');

  zip.file('xl/_rels/workbook.xml.rels',
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n' +
    '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
    sheets.map((s) => `<Relationship Id="rId${s.index}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${s.index}.xml"/>`).join('') +
    `<Relationship Id="rId${sheets.length + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>` +
    '</Relationships>');

  zip.file('xl/styles.xml', STYLES_XML);
  sheets.forEach((s, i) => zip.file(`xl/worksheets/sheet${s.index}.xml`, sheetXml({ ...s, selected: i === active })));

  return zip.generateAsync({
    type: 'uint8array',
    compression: 'DEFLATE',
    mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  });
}

export const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
